import React, { useState } from "react"
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap"
import { Link, useHistory } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"
import FsLightbox from "fslightbox-react"
import UserAvatar from "./UserAvatar"
import { Routes } from "@/routes"
import AssignUser from "@/components/Users/AssignUser"
import SchedulePlaceRouteModal from "@/components/Places/SchedulePlaceRouteModal"
import FeedCardBody from "./FeedCardBody"
import FeedComposer from "./FeedComposer"
import { FeedItem, TYPE_STYLE, fullUrl } from "./types"

interface FeedCardProps {
  item: FeedItem
  onCommentPosted: (visitId: string) => void
}

type Composer = "comment" | "note" | null
type Modal = "assign" | "revisit" | "route" | null

const localId = (item: FeedItem) => item.id.substring(item.id.indexOf(":") + 1)

const FeedCard = ({ item, onCommentPosted }: FeedCardProps) => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const [composer, setComposer] = useState<Composer>(null)
  const [modal, setModal] = useState<Modal>(null)
  const [posted, setPosted] = useState<{ kind: "comment" | "note"; text: string; at: string }[]>([])
  const [lightbox, setLightbox] = useState({ toggler: false, slide: 1 })

  const style = TYPE_STYLE[item.type]
  const place = item.place
  const visitId = item.type === "visit" ? localId(item) : item.type === "visit_comment" ? item.meta?.visitId : null
  const canComment = !!visitId
  const canNote = !!place && ["place_created", "place_note", "dispatch", "route_scheduled", "contact_created"].includes(item.type)
  const lat = item.meta?.lat ?? place?.lat
  const lng = item.meta?.lng ?? place?.lng
  const hasCoords = lat != null && lng != null && !(lat === 0 && lng === 0)
  const occurred = moment(item.occurredAt)
  const isERP = item.type === "dispatch"
  const actorName = item.actor?.name || (isERP ? "ERP" : t("Sistema"))
  const actorId = item.actor?.id
  const actorLink = actorId ? `${Routes.AgentDetails.clean}${actorId}` : null

  const filterByActor = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!actorId) return
    const params = new URLSearchParams(window.location.search)
    params.set("agent", actorId)
    params.delete("before")
    history.push(`${Routes.Feed.clean}?${params.toString()}`)
  }

  const openMedia = (index: number) => setLightbox(prev => ({ toggler: !prev.toggler, slide: index + 1 }))

  const handlePosted = (text: string) => {
    setPosted(prev => [...prev, { kind: composer as "comment" | "note", text, at: new Date().toISOString() }])
    if (composer === "comment" && visitId) onCommentPosted(visitId)
    setComposer(null)
  }

  return (
    <article className="feed-card" data-feed-id={item.id}>
      <div className="feed-avatar-col">
        {isERP ? (
          <div className="feed-avatar feed-avatar-erp" style={{ background: style.color }}>
            <i className="fas fa-truck" />
          </div>
        ) : actorLink ? (
          <Link to={actorLink} className="feed-avatar-link" title={t("Ver perfil")}>
            <UserAvatar name={actorName} image={item.actor?.image} color={item.actor?.color} />
          </Link>
        ) : (
          <UserAvatar name={actorName} image={item.actor?.image} color={item.actor?.color} />
        )}
      </div>

      <div className="feed-main">
        <header className="feed-header">
          {actorLink ? (
            <div className="d-inline-flex align-items-center gap-1">
              <Link to={actorLink} className="feed-actor feed-actor-link" title={t("Ver perfil")}>
                {actorName}
              </Link>
              <OverlayTrigger placement="top" overlay={<Tooltip>{t("Filtrar por este usuario")}</Tooltip>}>
                <button
                  type="button"
                  className="feed-actor-filter-btn"
                  onClick={filterByActor}
                  aria-label={t("Filtrar por este usuario")}
                >
                  <i className="fas fa-filter" />
                </button>
              </OverlayTrigger>
            </div>
          ) : (
            <span className="feed-actor">{actorName}</span>
          )}
          <span className="feed-type" style={{ color: style.color }}>
            <i className={`fas ${style.icon} me-1`} />
            {t(style.label)}
          </span>
          <OverlayTrigger placement="top" overlay={<Tooltip>{occurred.format(item.meta?.dateOnly ? "LL" : "LLLL")}</Tooltip>}>
            <span className="feed-time">· {item.meta?.dateOnly ? occurred.format("D MMM") : occurred.fromNow()}</span>
          </OverlayTrigger>
        </header>

        {place && (
          place.id ? (
            <Link to={Routes.PlaceDetails.clean + place.id} className="feed-place-chip">
              <i className="fas fa-map-marker-alt me-1" />
              <span className="text-truncate">{place.name}</span>
              {place.sector && <span className="text-muted ms-1 text-truncate">· {place.sector}</span>}
              {place.creditCode && <span className="text-muted ms-1">· {place.creditCode}</span>}
            </Link>
          ) : (
            <span className="feed-place-chip">
              <i className="fas fa-building me-1 text-muted" />
              <span className="text-truncate">{place.name}</span>
              {place.creditCode && <span className="text-muted ms-1">· {place.creditCode}</span>}
            </span>
          )
        )}

        <div className="feed-body">
          <FeedCardBody item={item} onOpenMedia={openMedia} />
        </div>

        {(place?.assignedToName || place?.revisitAssignedToName) && (
          <div className="feed-assignees">
            {place.assignedToName && (
              place.assignedTo ? (
                <Link
                  to={`${Routes.AgentDetails.clean}${place.assignedTo}`}
                  className="feed-assignee feed-assignee-link"
                  title={t("Ver perfil del responsable")}
                >
                  <i className="fas fa-user-tie" />
                  <UserAvatar name={place.assignedToName} color={place.assignedToColor} size={20} />
                  {place.assignedToName}
                </Link>
              ) : (
                <span className="feed-assignee" title={t("Responsable")}>
                  <i className="fas fa-user-tie" />
                  <UserAvatar name={place.assignedToName} color={place.assignedToColor} size={20} />
                  {place.assignedToName}
                </span>
              )
            )}
            {place.revisitAssignedToName && (
              place.revisitAssignedTo ? (
                <Link
                  to={`${Routes.AgentDetails.clean}${place.revisitAssignedTo}`}
                  className="feed-assignee feed-assignee-link"
                  title={t("Ver perfil de revisita")}
                >
                  <i className="fas fa-rotate" />
                  <UserAvatar name={place.revisitAssignedToName} color={place.revisitAssignedToColor} size={20} />
                  {place.revisitAssignedToName}
                </Link>
              ) : (
                <span className="feed-assignee" title={t("Revisita")}>
                  <i className="fas fa-rotate" />
                  <UserAvatar name={place.revisitAssignedToName} color={place.revisitAssignedToColor} size={20} />
                  {place.revisitAssignedToName}
                </span>
              )
            )}
          </div>
        )}

        {posted.length > 0 && (
          <div className="feed-thread">
            {posted.map((p, idx) => (
              <div key={idx} className="feed-thread-item">
                <i className={`fas ${p.kind === "comment" ? "fa-comment" : "fa-sticky-note"} me-2 text-muted`} />
                <span>{p.text}</span>
                <span className="text-muted small ms-2">{moment(p.at).fromNow()}</span>
              </div>
            ))}
          </div>
        )}

        {composer && (
          <FeedComposer
            mode={composer}
            visitId={visitId || undefined}
            placeId={place?.id}
            onPosted={handlePosted}
            onCancel={() => setComposer(null)}
          />
        )}

        <footer className="feed-actions">
          {canComment && (
            <Button variant="link" size="sm" onClick={() => setComposer(composer === "comment" ? null : "comment")}>
              <i className="far fa-comment me-1" />
              {item.commentCount > 0 ? item.commentCount : t("Comentar")}
            </Button>
          )}
          {canNote && (
            <Button variant="link" size="sm" onClick={() => setComposer(composer === "note" ? null : "note")}>
              <i className="far fa-sticky-note me-1" />
              {t("Nota")}
            </Button>
          )}
          {item.type === "incident_created" && (
            <Link
              to={`${Routes.IncidentDetails.clean}${localId(item)}`}
              className="btn btn-link btn-sm text-danger text-decoration-none"
            >
              <i className="fas fa-exclamation-triangle me-1" />
              {t("Ver incidente")}
            </Link>
          )}
          {item.type === "incident_created" && item.commentCount > 0 && (
            <Link
              to={`${Routes.IncidentDetails.clean}${localId(item)}`}
              className="btn btn-link btn-sm text-muted text-decoration-none"
            >
              <i className="far fa-comment me-1" />
              {item.commentCount} {item.commentCount === 1 ? t("comentario") : t("comentarios")}
            </Link>
          )}
          {place && place.id ? (
            <>
              <Button variant="link" size="sm" onClick={() => setModal("assign")}>
                <i className="fas fa-user-plus me-1" />
                {t("Asignar")}
              </Button>
              <Button variant="link" size="sm" onClick={() => setModal("revisit")}>
                <i className="fas fa-rotate me-1" />
                {t("Revisita")}
              </Button>
              <Button variant="link" size="sm" onClick={() => setModal("route")}>
                <i className="fas fa-route me-1" />
                {t("Poner en ruta")}
              </Button>
            </>
          ) : null}
          <span className="ms-auto d-flex">
            {hasCoords && (
              <a
                className="btn btn-link btn-sm"
                title={t("Ver en mapa")}
                href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-map" />
              </a>
            )}
            {place && place.id ? (
              <Link className="btn btn-link btn-sm" title={t("Ver lugar")} to={Routes.PlaceDetails.clean + place.id}>
                <i className="fas fa-arrow-up-right-from-square" />
              </Link>
            ) : null}
          </span>
        </footer>
      </div>

      {item.media.length > 0 && (
        <FsLightbox
          toggler={lightbox.toggler}
          sources={item.media.map(m => fullUrl(m.url))}
          slide={lightbox.slide}
          type="image"
        />
      )}

      {place && (modal === "assign" || modal === "revisit") && (
        <AssignUser
          show
          toggle={() => setModal(null)}
          type={modal === "revisit" ? "revisit" : undefined}
          place={{ id: place.id, name: place.name, assignedTo: place.assignedTo, revisitAssignedTo: place.revisitAssignedTo }}
        />
      )}

      {place && modal === "route" && (
        <SchedulePlaceRouteModal
          show
          onHide={() => setModal(null)}
          place={{
            id: place.id,
            name: place.name || "",
            address: place.address || undefined,
            sector: place.sector || undefined,
            assignedTo: place.assignedTo || undefined,
            assignedToObj: place.assignedToName ? { firstName: place.assignedToName } : undefined,
          }}
        />
      )}
    </article>
  )
}

export default FeedCard
